import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { type FC, memo } from "react";
import { useTranslation } from "react-i18next";
import ColorfulRole from "../../../components/UIToolkit/ColorfulRole/ColorfulRole";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import type { AlertAffectedNurse, AlertMismatchNurse } from "./types";
import { INSIGHT_BADGE_COLORS } from "./utils";

type Props = {
	nurse: AlertAffectedNurse;
	typeColors: { chipBg: string; text: string; iconBg: string };
};

export const AffectedNurseRow: FC<Props> = memo(({ nurse, typeColors }) => {
	const { t } = useTranslation();
	const { getNurseById } = useHospitalData();
	const formatPersonName = useFormatPersonName();

	const fullNurse = getNurseById(nurse.nurseId);
	const role = fullNurse?.role;
	const formattedName = fullNurse ? formatPersonName(fullNurse) : "";
	const displayName = formattedName || `#${nurse.nurseId}`;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				py: 0.5,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					minWidth: 0,
					maxWidth: "50%",
				}}
			>
				<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: typeColors.text, flexShrink: 0 }} />
				<Tooltip title={`${displayName}${role ? ` — ${role}` : ""}`} disableInteractive>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 0.75,
							minWidth: 0,
						}}
					>
						<Typography
							variant="body2"
							noWrap
							sx={{
								fontWeight: 600,
							}}
						>
							{displayName}
						</Typography>
						{role && <ColorfulRole role={role} />}
					</Box>
				</Tooltip>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					flexShrink: 0,
					flexWrap: "wrap",
					justifyContent: "flex-end",
				}}
			>
				{nurse.reason.split(", ").map((reason) => (
					<Chip
						key={reason}
						label={reason}
						size="small"
						sx={{
							bgcolor: typeColors.chipBg,
							color: typeColors.text,
							fontWeight: 500,
							fontSize: "0.65rem",
							height: 22,
						}}
					/>
				))}
				{/* Show at most 2 insight badges — more won't fit in the row */}
				{nurse.insights?.slice(0, 2).map((insight) => {
					const colors = INSIGHT_BADGE_COLORS[insight.insightType] ?? INSIGHT_BADGE_COLORS.scheduled_ot;
					const mismatchNurse = nurse as AlertMismatchNurse;
					const shiftTooltip =
						insight.insightType === "different_shift" && mismatchNurse.scheduledShiftType && mismatchNurse.actualShiftType
							? `${mismatchNurse.scheduledShiftType} → ${mismatchNurse.actualShiftType}`
							: "";
					return (
						<Tooltip key={insight.insightType} title={shiftTooltip} disableInteractive>
							<Chip
								label={t(insight.i18nKey, insight.i18nParams)}
								size="small"
								variant="outlined"
								sx={{
									borderColor: colors.border,
									color: colors.text,
									bgcolor: colors.bg,
									fontWeight: 500,
									fontSize: "0.6rem",
									height: 20,
								}}
							/>
						</Tooltip>
					);
				})}
			</Box>
		</Box>
	);
});
